/* eslint-disable no-underscore-dangle */
import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import ResourcesService from '../services/api/ResourcesService'
import Modal from './shared/Modal'
import FileInput from './shared/FileInput'
import { ErrorAlert } from './shared/Export'
import WarningAlert from './ui/WarningAlert'

const FileManagementModal = ({
  resourceId,
  resourceName,
  ensembleName,
  ensembleId,
  isOpen,
  onClose,
  variableIds = null
}) => {
  const [resourceData, setResourceData] = useState(null)
  const [files, setFiles] = useState({})
  const [valuesAttributes, setValuesAttributes] = useState({})
  const [filesToDestroy, setFilesToDestroy] = useState({})
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})

  useEffect(() => {
    // Fetch full resource data when modal opens
    if (isOpen && resourceId && ensembleId) {
      setLoading(true)
      ResourcesService.getResource(ensembleId, resourceId)
        .then(data => {
          setResourceData(data)
          // Initialize values_attributes for existing values
          const initialValues = {}
          data.values.forEach(value => {
            initialValues[value.id] = {
              content: value.content || ''
            }
          })
          setValuesAttributes(initialValues)
          setLoading(false)
        })
        .catch(() => {
          setLoading(false)
        })
    }

    // Clear state when modal closes
    if (!isOpen) {
      setFiles({})
      setValuesAttributes({})
      setFilesToDestroy({})
      setSaving(false)
      setError(null)
      setFieldErrors({})
      setResourceData(null)
    }
  }, [isOpen, resourceId, ensembleId])

  const handleFileSelect = useCallback(valueId => file => {
    setFiles(prevFiles => ({
      ...prevFiles,
      [valueId]: file
    }))
    // Clear destroy flag when new file is selected
    setFilesToDestroy(prev => {
      const newAttrs = { ...prev }
      delete newAttrs[valueId]
      return newAttrs
    })
  }, [])

  const handleFileDelete = useCallback(valueId => () => {
    // Clear the new file selection
    setFiles(prev => {
      const newFiles = { ...prev }
      delete newFiles[valueId]
      return newFiles
    })

    // Mark existing file attachment for deletion
    const value = resourceData?.values.find(val => val.id === valueId)
    if (value?.file?.id) {
      setFilesToDestroy(prev => ({
        ...prev,
        [valueId]: {
          id: value.file.id,
          _destroy: '1'
        }
      }))
    }
  }, [resourceData])

  const handleSave = useCallback(async() => {
    if (!resourceData)
      return

    setSaving(true)

    try {
      // Build FormData
      const submitData = new FormData()
      submitData.append('resource[name]', resourceData.name)

      if (resourceData.has_address)
        submitData.append('resource[address]', resourceData.address || '')


      // Add user IDs
      if (resourceData.user_ids?.length > 0) {
        resourceData.user_ids.forEach(userId => {
          submitData.append('resource[user_ids][]', userId)
        })
      } else
        submitData.append('resource[user_ids][]', '')


      // Add values attributes
      resourceData.values.forEach(value => {
        const valueId = value.id
        submitData.append(`resource[values_attributes][${valueId}][id]`, valueId)
        submitData.append(`resource[values_attributes][${valueId}][variable_id]`, value.variable_id)
        const content = valuesAttributes[valueId]?.content || value.content || ''
        submitData.append(`resource[values_attributes][${valueId}][content]`, content)

        // Add file if selected
        if (files[valueId])
          submitData.append(`resource[values_attributes][${valueId}][file]`, files[valueId])


        // Set file to empty if marked for destruction
        if (filesToDestroy[valueId])
          submitData.append(`resource[values_attributes][${valueId}][file]`, '')
      })

      const response = await ResourcesService.updateResource(ensembleId, resourceId, submitData)

      if (response.success === false) {
        setSaving(false)
        setError(response.errors?.join(', ') || 'Erreur lors de la sauvegarde des fichiers')
        setFieldErrors(response.formatted_errors || {})
        return
      }

      // Reload page to show updated file counts
      window.location.reload()
    } catch {
      setSaving(false)
      setError('Erreur lors de la sauvegarde des fichiers')
    }
  }, [resourceData, files, valuesAttributes, filesToDestroy, ensembleId, resourceId])

  const getFileDisplay = useCallback(valueId => {
    // Check for new file first
    if (files[valueId])
      return files[valueId].name


    // Check for existing file (not marked for deletion)
    const value = resourceData?.values.find(val => val.id === valueId)
    if (value?.file && !filesToDestroy[valueId]?._destroy)
      return value.file.filename


    return ''
  }, [files, resourceData, filesToDestroy])

  const getValueErrors = useCallback(valueId => {
    // FieldErrors format: { "values[17]": { "file": ["error message"] } }
    const key = `values[${valueId}]`
    return fieldErrors[key]?.file || null
  }, [fieldErrors])

  const handleClose = useCallback(() => {
    if (onClose)
      onClose()
  }, [onClose])

  const modalFooter = (
    <>
      <button
        type="button"
        className="btn btn-outline-light text-dark border border-muted font-weight-bolder"
        onClick={handleClose}
      >
        Annuler
      </button>
      <button
        type="button"
        className="btn btn-success font-weight-bolder"
        disabled={saving}
        onClick={handleSave}
      >
        {saving ? 'Enregistrement...' : 'Enregistrer'}
      </button>
    </>
  )

  const modalTitle = (
    <>
      <i className="fas fa-upload" />
      <span className="ml-2">
        Ajouter un fichier sur une ressource
      </span>
    </>
  )

  return (
    <Modal
      isOpen={isOpen}
      title={modalTitle}
      footer={modalFooter}
      onClose={handleClose}
    >
      <div className="bg-white border p-3 mb-3">
        <p className="mb-1">
          <strong>Ensemble :</strong>
          {' '}
          {ensembleName}
        </p>
        <p className="mb-0">
          <strong>Ressource :</strong>
          {' '}
          {resourceName}
        </p>
      </div>
      {error ? <ErrorAlert message={error} /> : null}
      <WarningAlert>
        <strong>Attention :</strong>
        {' '}
        Modifier ou supprimer un fichier dans une ressource peut avoir des
        conséquences sur toutes les collectes où ce fichier apparaît.
      </WarningAlert>
      {loading ? (
        <div className="text-center">
          <span>Chargement...</span>
        </div>
      ) : resourceData?.values
        .filter(val => ['pdf', 'image'].includes(val.variable.variable_type))
        .filter(val => !variableIds || variableIds.includes(val.variable.id))
        .map(value => {
          const isMarkedForDeletion = filesToDestroy[value.id]?._destroy === '1'

          return (
            <div key={value.id} className="form-group mb-0 mt-3">
              <label>
                Valeur pour
                {' '}
                {value.variable.name}
              </label>
              <FileInput
                accept={value.variable.variable_type === 'pdf' ? '.pdf' : 'image/png,image/jpeg,image/jpg'}
                placeholder="Aucun fichier sélectionné"
                buttonText="Choisir un fichier"
                buttonClass="btn-success"
                errors={getValueErrors(value.id)}
                value={getFileDisplay(value.id)}
                onChange={handleFileSelect(value.id)}
                onDelete={handleFileDelete(value.id)}
              />
              {isMarkedForDeletion ? (
                <small className="text-danger">
                  Ce fichier sera supprimé lors de l&apos;enregistrement
                </small>
              ) : null}
            </div>
          )
        })}
    </Modal>
  )
}

FileManagementModal.propTypes = {
  ensembleId: PropTypes.number.isRequired,
  ensembleName: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  resourceId: PropTypes.number.isRequired,
  resourceName: PropTypes.string.isRequired
}

export default FileManagementModal